import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, MagnifyingGlass, ClipboardText, Warning, CheckCircle,
  Clock, ArrowsClockwise, Users as UsersIcon, Bookmark, BookmarkSimple, Trash, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import TaskDetailDialog from "@/components/TaskDetailDialog";
import NewTaskDialog from "@/components/NewTaskDialog";

const SECTIONS = [
  { key: "overdue", label: "Overdue", icon: Warning, tone: "red" },
  { key: "due_today", label: "Due Today", icon: Clock, tone: "amber" },
  { key: "open", label: "Open", icon: ClipboardText, tone: "blue" },
  { key: "in_progress", label: "In Progress", icon: ArrowsClockwise, tone: "indigo" },
  { key: "completed", label: "Recently Completed", icon: CheckCircle, tone: "emerald" },
];

const TONE = {
  red: "bg-red-50 text-red-800 border-red-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  blue: "bg-blue-50 text-blue-800 border-blue-200",
  indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  slate: "bg-slate-50 text-slate-800 border-slate-200",
};

const today = () => new Date().toISOString().slice(0, 10);

// Task status pipeline — used by the bulk-action bar and per-row status
// select. Values MUST match the backend `TaskStatusLiteral`.
const STATUS_OPTIONS = ["Open", "In Progress", "Waiting", "Completed", "Cancelled"];

// LocalStorage key generator for per-user Saved Filter presets (P5).
// Namespaced by email so multiple staff on a shared browser stay isolated.
const SAVED_FILTERS_STORAGE_KEY = (email) => `foc.tasks.savedFilters.${email || "anon"}`;

export default function Tasks() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "team" ? "team" : "mine";
  const initialSection = params.get("section") || "";
  const initialTaskType = params.get("task_type") || "";
  const [tab, setTab] = useState(initialTab);
  const [sectionFilter, setSectionFilter] = useState(initialSection);
  const [taskTypeFilter, setTaskTypeFilter] = useState(initialTaskType);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openTask, setOpenTask] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  // Bulk-select state for the action bar shown when >0 tasks are ticked.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-row inline status change busy indicator (holds the busy task id).
  const [rowBusy, setRowBusy] = useState(null);
  // P5 · Saved Filter presets — persisted to localStorage per user.
  const [savedFilters, setSavedFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(SAVED_FILTERS_STORAGE_KEY(user?.email));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const canSeeTeam = ["Administrator", "Owner", "Office Staff"].includes(user?.role);
  const canCreate = ["Administrator", "Owner", "Office Staff"].includes(user?.role);

  // Employees should never see tasks assigned to other users. If somebody
  // deep-links to ?tab=team without the required role, silently downgrade to
  // the "mine" scope so we never leak another user's workload.
  useEffect(() => {
    if (tab === "team" && !canSeeTeam) {
      setTab("mine");
      const p = new URLSearchParams(params);
      p.delete("tab");
      setParams(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, canSeeTeam]);

  const load = async () => {
    setLoading(true);
    try {
      const scope = tab === "team" && canSeeTeam ? "team" : "mine";
      const res = await api.get(`/tasks`, {
        params: {
          scope,
          q: q || undefined,
          task_type: taskTypeFilter || undefined,
        },
      });
      setTasks(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, taskTypeFilter]);

  // Refresh on query change (debounced)
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // P5 · Persist saved filter presets whenever they change.
  useEffect(() => {
    try {
      localStorage.setItem(
        SAVED_FILTERS_STORAGE_KEY(user?.email),
        JSON.stringify(savedFilters),
      );
    } catch { /* quota exceeded — silent */ }
  }, [savedFilters, user?.email]);

  const applyPreset = (p) => {
    setTab(p.tab || "mine");
    setSectionFilter(p.sectionFilter || "");
    setTaskTypeFilter(p.taskTypeFilter || "");
    setQ(p.q || "");
    const next = new URLSearchParams();
    if (p.tab === "team") next.set("tab", "team");
    if (p.sectionFilter) next.set("section", p.sectionFilter);
    if (p.taskTypeFilter) next.set("task_type", p.taskTypeFilter);
    setParams(next);
  };

  const savePreset = () => {
    const label = window.prompt("Name this saved view:", "");
    if (!label) return;
    const snap = {
      id: `${Date.now()}`, label: label.trim(),
      tab, sectionFilter, taskTypeFilter, q,
    };
    setSavedFilters((prev) => [...prev.filter((x) => x.label !== snap.label), snap]);
    toast.success(`Saved view: ${snap.label}`);
  };

  const deletePreset = (id) => {
    setSavedFilters((prev) => prev.filter((x) => x.id !== id));
  };

  const bulkSetStatus = async (status) => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} task(s) as "${status}"?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/tasks/bulk-status", {
        task_ids: Array.from(selected),
        status,
      });
      const msg = `${r.data.count_updated} updated${r.data.count_skipped ? `, ${r.data.count_skipped} skipped` : ""}`;
      (r.data.count_updated > 0 ? toast.success : toast.warning)(msg);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const rowSetStatus = async (task, status) => {
    if (task.status === status) return;
    setRowBusy(task.id);
    try {
      const r = await api.patch(`/tasks/${task.id}`, { status });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    } finally {
      setRowBusy(null);
    }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    if (selected.size === tasks.length && tasks.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tasks.map((t) => t.id)));
    }
  };


  const grouped = useMemo(() => groupTasks(tasks), [tasks]);

  const openTaskId = params.get("task");
  useEffect(() => {
    if (!openTaskId) return;
    api.get(`/tasks/${openTaskId}`).then((r) => setOpenTask(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTaskId]);

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title="Tasks"
        description={tab === "team" ? "Tasks assigned across the team" : "Your assigned tasks"}
        rightSlot={
          <div className="flex items-center gap-2">
            {["Administrator", "Owner"].includes(user?.role) && (
              <button
                data-testid="tasks-report-button"
                onClick={() => (window.location.href = "/tasks/report")}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                SLA Report
              </button>
            )}
            {canCreate && (
              <button
                data-testid="new-task-button"
                onClick={() => setNewOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#EB1D25] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#AF101B]"
              >
                <Plus size={14} weight="bold" /> New Task
              </button>
            )}
          </div>
        }
      />

      {canSeeTeam && (
        <div className="mb-4 flex gap-1 border-b border-gray-200">
          {[
            { k: "mine", label: "My Tasks" },
            { k: "team", label: "Team Tasks", icon: UsersIcon },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => { setTab(t.k); setParams(t.k === "mine" ? {} : { tab: "team" }); }}
              className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
                tab === t.k
                  ? "border-[#EB1D25] text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {t.icon && <t.icon size={14} weight="bold" />}
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            data-testid="tasks-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by task #, subject, assignee, customer, status…"
            className="w-full rounded-md border border-gray-300 bg-white px-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1D25]/50"
          />
        </div>
        {sectionFilter && (
          <button
            data-testid="clear-section-filter"
            onClick={() => { setSectionFilter(""); const p = new URLSearchParams(params); p.delete("section"); setParams(p); }}
            className="rounded-full bg-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-300"
          >
            Filter: {sectionFilter.replace("_", " ")} ✕
          </button>
        )}
        <button
          onClick={load}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>

      {/* P5 · Saved filter presets — user-local, persisted in localStorage. */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="saved-filters-bar">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-ink-muted">
          Saved views
        </span>
        {savedFilters.length === 0 && (
          <span className="text-xs text-ink-muted">
            None yet. Configure filters above, then Save current view.
          </span>
        )}
        {savedFilters.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-divider bg-surface-alt px-2.5 py-1 text-xs">
            <button
              data-testid={`saved-filter-apply-${p.id}`}
              onClick={() => applyPreset(p)}
              className="font-medium text-ink hover:text-brand"
            >
              <Bookmark size={11} weight="fill" className="-mt-0.5 mr-1 inline text-brand" />
              {p.label}
            </button>
            <button
              data-testid={`saved-filter-delete-${p.id}`}
              onClick={() => deletePreset(p.id)}
              className="text-ink-muted hover:text-status-error-fg"
              aria-label={`Delete ${p.label}`}
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
        <button
          data-testid="save-current-filter"
          onClick={savePreset}
          className="inline-flex items-center gap-1 rounded-full border border-divider-strong px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt"
        >
          <BookmarkSimple size={12} weight="bold" /> Save current view
        </button>
      </div>

      {/* P5 · Bulk action toolbar (appears when at least one task selected). */}
      {selected.size > 0 && (
        <div
          data-testid="bulk-actions-toolbar"
          className="sticky top-16 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-md border border-brand bg-brand/5 px-3 py-2 shadow-token-sm"
        >
          <span className="text-sm font-semibold text-charcoal" data-testid="bulk-actions-count">
            {selected.size} selected
          </span>
          <button
            data-testid="bulk-clear-selection"
            onClick={() => setSelected(new Set())}
            className="rounded-md border border-divider-strong bg-surface px-2 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt"
          >
            Clear
          </button>
          <span className="text-ink-muted">·</span>
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              data-testid={`bulk-set-status-${s.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => bulkSetStatus(s)}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1 rounded-md border border-divider-strong bg-surface px-2.5 py-1 text-xs font-medium text-ink-secondary hover:bg-surface-alt disabled:opacity-50"
            >
              Set to {s}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="rounded-md border border-gray-200 bg-white p-6 text-sm text-gray-500">
          Loading tasks…
        </div>
      ) : tasks.length === 0 ? (
        <div
          data-testid="tasks-empty"
          className="rounded-md border border-gray-200 bg-white p-10 text-center text-sm text-gray-500"
        >
          {tab === "team" ? "No team tasks yet." : "You have no tasks assigned. 🎉"}
        </div>
      ) : (
        <div className="space-y-6">
          {SECTIONS.filter((s) => !sectionFilter || s.key === sectionFilter).map((s) => {
            const rows = grouped[s.key] || [];
            if (rows.length === 0) return null;
            const Icon = s.icon;
            return (
              <section key={s.key} data-testid={`section-${s.key}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${TONE[s.tone]}`}>
                    <Icon size={12} weight="bold" /> {s.label}
                  </span>
                  <span className="font-mono text-xs text-gray-500">{rows.length}</span>
                </div>
                <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-[#f9fafb] text-left text-xs uppercase tracking-wider text-gray-500">
                        <th className="w-8 px-2 py-1.5">
                          <input
                            type="checkbox"
                            data-testid={`section-${s.key}-select-all`}
                            aria-label="Select all"
                            checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                            onChange={() => {
                              const allInSection = rows.every((r) => selected.has(r.id));
                              const next = new Set(selected);
                              rows.forEach((r) => allInSection ? next.delete(r.id) : next.add(r.id));
                              setSelected(next);
                            }}
                            className="h-4 w-4 rounded border-divider-strong text-brand focus:ring-brand"
                          />
                        </th>
                        <th className="px-3 py-1.5 font-semibold">#</th>
                        <th className="px-3 py-1.5 font-semibold">Subject</th>
                        <th className="px-3 py-1.5 font-semibold">Type</th>
                        {tab === "team" && <th className="px-3 py-1.5 font-semibold">Assignee</th>}
                        <th className="px-3 py-1.5 font-semibold">Due</th>
                        <th className="px-3 py-1.5 font-semibold">Priority</th>
                        <th className="px-3 py-1.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => (
                        <tr
                          key={t.id}
                          data-testid={`task-row-${t.task_number}`}
                          className={`border-b border-gray-100 last:border-0 ${selected.has(t.id) ? "bg-brand/5" : "hover:bg-gray-50"}`}
                        >
                          <td className="w-8 px-2 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              data-testid={`task-select-${t.task_number}`}
                              checked={selected.has(t.id)}
                              onChange={() => toggleSelected(t.id)}
                              aria-label={`Select ${t.task_number}`}
                              className="h-4 w-4 rounded border-divider-strong text-brand focus:ring-brand"
                            />
                          </td>
                          <td
                            className="cursor-pointer px-3 py-2 font-mono text-xs text-gray-600"
                            onClick={() => { setParams({ ...(tab==="team"?{tab:"team"}:{}), task: t.id }); setOpenTask(t); }}
                          >{t.task_number}</td>
                          <td
                            className="cursor-pointer px-3 py-2 text-gray-900"
                            onClick={() => { setParams({ ...(tab==="team"?{tab:"team"}:{}), task: t.id }); setOpenTask(t); }}
                          >{t.subject}</td>
                          <td className="px-3 py-2 text-gray-700">{t.task_type}</td>
                          {tab === "team" && <td className="px-3 py-2 text-gray-700">{t.assigned_to_name}</td>}
                          <td className="px-3 py-2 font-mono text-xs text-gray-700">{t.due_date || "—"}</td>
                          <td className="px-3 py-2"><PriorityChip p={t.priority} /></td>
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <InlineStatusSelect
                              value={t.status}
                              disabled={rowBusy === t.id}
                              onChange={(v) => rowSetStatus(t, v)}
                              testId={`inline-status-${t.task_number}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {openTask && (
        <TaskDetailDialog
          task={openTask}
          onClose={() => { setOpenTask(null); const p = new URLSearchParams(params); p.delete("task"); setParams(p); }}
          onChanged={() => { load(); if (openTaskId) api.get(`/tasks/${openTaskId}`).then((r) => setOpenTask(r.data)); }}
        />
      )}
      {newOpen && (
        <NewTaskDialog
          onClose={() => setNewOpen(false)}
          onCreated={() => { setNewOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function groupTasks(tasks) {
  const t = today();
  const out = { overdue: [], due_today: [], open: [], in_progress: [], completed: [] };
  for (const task of tasks) {
    if (task.status === "Completed") { out.completed.push(task); continue; }
    if (task.status === "Cancelled") continue;
    if (task.due_date && task.due_date < t) { out.overdue.push(task); continue; }
    if (task.due_date === t) { out.due_today.push(task); continue; }
    if (task.status === "In Progress") { out.in_progress.push(task); continue; }
    out.open.push(task);
  }
  return out;
}

function StatusChip({ s }) {
  const map = {
    "Open": "bg-blue-100 text-blue-800",
    "In Progress": "bg-indigo-100 text-indigo-800",
    "Waiting": "bg-amber-100 text-amber-800",
    "Completed": "bg-emerald-100 text-emerald-800",
    "Cancelled": "bg-gray-100 text-gray-600",
  };
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${map[s] || "bg-gray-100"}`}>{s}</span>;
}
function PriorityChip({ p }) {
  const map = { Low: "text-gray-500", Normal: "text-gray-700", High: "text-amber-700", Urgent: "text-red-700 font-semibold" };
  return <span className={`text-xs ${map[p] || "text-gray-700"}`}>{p}</span>;
}

function InlineStatusSelect({ value, disabled, onChange, testId }) {
  // A styled <select> that renders the status pill by default. Click to reveal
  // the native picker; onChange fires an immediate PATCH via `onChange`.
  const tone = {
    "Open": "bg-status-info-bg text-status-info-fg",
    "In Progress": "bg-dept-fleet/10 text-dept-fleet",
    "Waiting": "bg-status-warning-bg text-status-warning-fg",
    "Completed": "bg-status-success-bg text-status-success-fg",
    "Cancelled": "bg-surface-alt text-ink-secondary",
  }[value] || "bg-surface-alt text-ink-secondary";
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`cursor-pointer rounded px-1.5 py-0.5 text-xs font-semibold focus:outline-none focus-visible:shadow-token-focus disabled:opacity-60 ${tone}`}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s}>{s}</option>
      ))}
    </select>
  );
}

